"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { Eyebrow, displayFont } from "@/components/content-blocks/band-helpers";
import { ProductCard } from "@/components/product-card";
import type { ContentBlock } from "@/lib/storefront-types";

type ProductSpotlightSectionProps = {
  block: Extract<ContentBlock, { kind: "productSpotlight" }>;
};

function ArrowIcon({ direction }: { direction: "left" | "right" }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.2}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-6 w-6"
    >
      {direction === "left" ? (
        <path d="M15 18l-6-6 6-6" />
      ) : (
        <path d="M9 18l6-6-6-6" />
      )}
    </svg>
  );
}

export function ProductSpotlightSection({
  block,
}: ProductSpotlightSectionProps) {
  const isLight = block.appearance === "light";
  const isFrozen = block.appearance === "frozen";
  const scrollRef = useRef<HTMLDivElement>(null);
  const animationFrameRef = useRef<number | null>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);

  useEffect(() => {
    updateScrollState();
    const el = scrollRef.current;
    if (!el) return;

    const observer = new ResizeObserver(updateScrollState);
    observer.observe(el);
    return () => {
      observer.disconnect();
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [updateScrollState]);

  const animateScrollTo = useCallback(
    (el: HTMLDivElement, targetLeft: number) => {
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
      }

      const startLeft = el.scrollLeft;
      const delta = targetLeft - startLeft;
      if (Math.abs(delta) < 1) {
        el.scrollLeft = targetLeft;
        updateScrollState();
        return;
      }

      const durationMs = 420;
      const initialSnapType = el.style.scrollSnapType || "x mandatory";
      const startTime = performance.now();

      el.style.scrollSnapType = "none";

      const step = (timestamp: number) => {
        const progress = Math.min((timestamp - startTime) / durationMs, 1);
        const easedProgress =
          progress < 0.5
            ? 4 * progress * progress * progress
            : 1 - Math.pow(-2 * progress + 2, 3) / 2;

        el.scrollLeft = startLeft + delta * easedProgress;
        updateScrollState();

        if (progress < 1) {
          animationFrameRef.current = requestAnimationFrame(step);
          return;
        }

        el.scrollLeft = targetLeft;
        el.style.scrollSnapType = initialSnapType;
        updateScrollState();
        animationFrameRef.current = null;
      };

      animationFrameRef.current = requestAnimationFrame(step);
    },
    [updateScrollState],
  );

  function scrollBy(direction: "left" | "right") {
    const el = scrollRef.current;
    if (!el) return;

    const cards = Array.from(el.children).filter(
      (child): child is HTMLDivElement => child instanceof HTMLDivElement,
    );
    if (cards.length === 0) return;

    const currentIndex = cards.reduce((closestIndex, card, index) => {
      const closestCard = cards[closestIndex];
      const currentDistance = Math.abs(card.offsetLeft - el.scrollLeft);
      const closestDistance = Math.abs(closestCard.offsetLeft - el.scrollLeft);
      return currentDistance < closestDistance ? index : closestIndex;
    }, 0);

    const targetIndex =
      direction === "left"
        ? Math.max(0, currentIndex - 1)
        : Math.min(cards.length - 1, currentIndex + 1);

    const targetCard = cards[targetIndex];
    animateScrollTo(el, Math.max(0, targetCard.offsetLeft - 4));
  }

  const sectionClass = isLight
    ? "border-y storefront-border-soft bg-[#f8fbf5] -mt-px"
    : isFrozen
      ? "storefront-surface-frozen"
      : "storefront-surface-dark";
  const headingClass = isLight ? "storefront-text-heading" : "text-white";

  return (
    <section className={`relative overflow-hidden py-10 sm:py-14 ${sectionClass}`}>
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        {block.headline || block.sectionTitle ? (
          <div className="pb-8 sm:pb-10">
            <div className="max-w-[56rem]">
              {block.sectionTitle && block.headline ? (
                <Eyebrow
                  inverse={!isLight}
                  className={isLight ? "storefront-text-muted" : ""}
                >
                  {block.sectionTitle}
                </Eyebrow>
              ) : null}
              {block.headline ? (
                <h2
                  className={`${displayFont.className} ${
                    block.sectionTitle ? "mt-4" : ""
                  } text-[clamp(1.85rem,3.6vw,3.2rem)] font-bold leading-[1.06] tracking-tight ${headingClass}`}
                >
                  {block.headline}
                </h2>
              ) : null}
              {!block.headline && block.sectionTitle ? (
                <h2
                  className={`${displayFont.className} text-[clamp(1.85rem,3.6vw,3.2rem)] font-bold leading-[1.06] tracking-tight ${headingClass}`}
                >
                  {block.sectionTitle}
                </h2>
              ) : null}
            </div>
          </div>
        ) : null}

        <div className="relative">
          {/* Arrow buttons — visible on all screen sizes */}
          {canScrollLeft ? (
            <button
              type="button"
              onClick={() => scrollBy("left")}
              className="absolute -left-1 top-1/2 z-20 inline-flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-[var(--storefront-primary)] bg-[var(--storefront-primary)] text-white shadow-[0_14px_28px_-18px_rgba(0,39,0,0.5)] transition-colors hover:bg-white hover:text-[var(--storefront-primary)] sm:h-[3.2rem] sm:w-[3.2rem]"
              aria-label="Scroll left"
            >
              <ArrowIcon direction="left" />
            </button>
          ) : null}
          {canScrollRight ? (
            <button
              type="button"
              onClick={() => scrollBy("right")}
              className="absolute -right-1 top-1/2 z-20 inline-flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-[var(--storefront-primary)] bg-[var(--storefront-primary)] text-white shadow-[0_14px_28px_-18px_rgba(0,39,0,0.5)] transition-colors hover:bg-white hover:text-[var(--storefront-primary)] sm:h-[3.2rem] sm:w-[3.2rem]"
              aria-label="Scroll right"
            >
              <ArrowIcon direction="right" />
            </button>
          ) : null}

          {/* Scrollable product row — scrollbar hidden, arrow-only navigation */}
          <div
            ref={scrollRef}
            onScroll={updateScrollState}
            className="flex gap-4 overflow-x-auto px-3 pb-2 pt-5 lg:px-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            role="list"
            style={{
              scrollSnapType: "x mandatory",
              WebkitOverflowScrolling: "touch",
            }}
          >
            {block.spotlightProducts.map((product) => (
              <div
                key={product.id}
                role="listitem"
                className="w-[85vw] flex-shrink-0 sm:w-[54vw] md:w-[42vw] lg:w-[calc((100%_-_1rem)_/_2)]"
                style={{ scrollSnapAlign: "start", scrollSnapStop: "always" }}
              >
                <ProductCard product={product} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
